import { useState } from "react";
import BackIcon from "components/icons/BackIcon";
import InputField from "components/fields/InputField";
import Select from "components/fields/Select";
import DarkButton from "components/actions/DarkButton";
import WhiteButton from "components/actions/WhiteButton";
import PlusIcon from "components/icons/PlusIcon";
import PenEditIcon from "components/icons/PenEditIcon";
import CheckIcon from "components/icons/Check";
import { useNavigate, useSearchParams } from "react-router-dom";
import AtriumApi from "api";
import Avatar from "components/avatar";
import AlertPane from "components/alert";
import NewChildForm from "./new-child-form";

const Registration = () => {
  const [step, setStep] = useState("parent-details");

  const navigate = useNavigate();
  const [queryParams] = useSearchParams();
  const phone = queryParams.get("phone");

  const [user, setUser] = useState({ phone: phone });
  const [guardians, setGuardians] = useState([]);
  const [children, setChildren] = useState([]);
  const [errors, setErrors] = useState([]);

  const onSubmit = (e) => {
    e.preventDefault();
    setErrors([]);
    const formData = new FormData();
    for (const key in user) {
      formData.append(key, user[key]);
    }
    AtriumApi.post("/v1/users/register", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }).then((resp) => {
      if (resp.status === 422) {
        setErrors(resp.data.errors);
        return;
      }
      if (step === "parent-details") {
        const nextStep = guardians.length > 0 ? "overview" : "kid-details";
        setGuardians([
          ...guardians.filter((x) => x.id !== resp.data.id),
          resp.data,
        ]);
        setUser({ related: phone, role: "child" });
        setStep(nextStep);
      } else if (step === "kid-details") {
        setChildren([
          ...children.filter((x) => x.id !== resp.data.id),
          resp.data,
        ]);
        setStep("overview");
      } else {
        throw new Error("unreachable");
      }
    });
  };

  return (
    <>
      {step === "parent-details" && (
        <form
          onSubmit={() => onSubmit(user)}
          noValidate={true}
          className="flex flex-col gap-6 px-4 py-2"
        >
          <div>
            <button
              onClick={() => navigate(`/member/check-in?phone=${phone}`)}
              className="bg-white text-navy-900 px-4 py-3 rounded-xl"
            >
              <BackIcon />
            </button>
          </div>
          <p className="text-3xl font-bold">
            {guardians.length
              ? "Add parent / guardian"
              : "Please register to continue"}
          </p>
          {errors.length ? (
            <AlertPane state="error">
              Sorry, we have trouble to save your changes
            </AlertPane>
          ) : (
            ""
          )}
          <div className="flex flex-col gap-4 mb-4">
            <p className="text-lg">Parent details</p>
            <InputField
              placeholder="First name"
              value={user.firstName || ""}
              onChange={(e) => setUser({ ...user, firstName: e.target.value })}
              state={
                errors.findIndex((error) => error.path === "firstName") !== -1
                  ? "error"
                  : ""
              }
            />
            <InputField
              placeholder="Last name"
              value={user.lastName || ""}
              onChange={(e) => setUser({ ...user, lastName: e.target.value })}
              state={
                errors.findIndex((error) => error.path === "lastName") !== -1
                  ? "error"
                  : ""
              }
            />
            <InputField
              placeholder="Email"
              value={user.email || ""}
              onChange={(e) => setUser({ ...user, email: e.target.value })}
              state={
                errors.findIndex((error) => error.path === "email") !== -1
                  ? "error"
                  : ""
              }
            />
            <InputField
              placeholder="Phone"
              value={user.phone || ""}
              onChange={(e) => setUser({ ...user, phone: e.target.value })}
              state={
                errors.findIndex((error) => error.path === "phone") !== -1
                  ? "error"
                  : ""
              }
            />
            <Select
              placeholder="Gender"
              required
              value={user.gender || ""}
              onChange={(e) => setUser({ ...user, gender: e.target.value })}
              state={
                errors.findIndex((error) => error.path === "gender") !== -1
                  ? "error"
                  : ""
              }
            >
              <option value="" disabled hidden>
                Gender
              </option>
              <option value="na">Prefer not to tell</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Non-binary</option>
            </Select>
          </div>

          <DarkButton type="submit">Continue</DarkButton>
        </form>
      )}

      {step === "kid-details" && (
        <div className="flex flex-col gap-6 px-4 py-2">
          <div>
            <button
              onClick={() => navigate(`/member/check-in?phone=${phone}`)}
              className="bg-white text-navy-900 px-4 py-3 rounded-xl"
            >
              <BackIcon />
            </button>
          </div>
          <p className="text-3xl font-bold">Please register to continue</p>
          {errors.length ? (
            <AlertPane state="error">
              Sorry, we have trouble to save your changes
            </AlertPane>
          ) : (
            ""
          )}

          <NewChildForm onSubmit={onSubmit} errors={errors} />
        </div>
      )}

      {step === "overview" && (
        <div className="flex flex-col gap-6 px-4 py-2">
          <div>
            <button
              onClick={() => navigate(`/member/check-in?phone=${phone}`)}
              className="bg-white text-navy-900 px-4 py-3 rounded-xl"
            >
              <BackIcon />
            </button>
          </div>
          <p className="text-3xl font-bold">
            Please check your registration details.
          </p>

          <div className="flex flex-col gap-4 mb-4">
            <p className="text-lg">Parents / Guardians</p>

            {guardians.map((user) => (
              <div
                className="flex flex-row items-center rounded-2xl px-5 py-4 bg-white dark:bg-navy-700"
                key={"guardian" + user.id}
              >
                <div className="flex-1">
                  <p className="text-base font-bold mb-1">
                    {user.firstName} {user.lastName}
                  </p>
                  <p className="text-sm text-gray-700">{user.phone}</p>
                </div>
                <button className="px-4">
                  <PenEditIcon />
                </button>
              </div>
            ))}

            <div>
              <WhiteButton
                onClick={() => {
                  setUser({ related: phone, role: "guardian" });
                  setStep("parent-details");
                }}
              >
                <span className="inline-block align-middle mr-2">
                  <PlusIcon />
                </span>
                <span className="inline-block align-middle">
                  Add parent/guardian
                </span>
              </WhiteButton>
            </div>
          </div>

          <div className="flex flex-col gap-4 mb-4">
            <p className="text-lg">Children</p>
            {children.map((child, i) => (
              <div
                key={"child" + i}
                className={`bg-white select-none dark:bg-navy-700 flex flex-row items-center p-2 rounded-xl gap-6`}
              >
                {<Avatar user={child} />}
                <div className="flex-1">
                  <p className="font-bold text-normal mb-1">
                    {child.firstName} {child.lastName}
                  </p>
                  <p className="text-gray-700 text-sm">
                    {new Date().getFullYear() -
                      new Date(child.dob).getFullYear()}{" "}
                    years
                  </p>
                </div>
                <button className="px-4">
                  <PenEditIcon />
                </button>
              </div>
            ))}
            <div>
              <WhiteButton
                onClick={() => {
                  setUser({ related: phone, role: "child" });
                  setStep("kid-details");
                }}
              >
                <span className="inline-block align-middle mr-2">
                  <PlusIcon />
                </span>
                <span className="inline-block align-middle">Add Child</span>
              </WhiteButton>
            </div>
          </div>

          <DarkButton>Confirm and register</DarkButton>
        </div>
      )}

      {step === "finished" && (
        <div className="flex-1 flex flex-col gap-6 px-4 pt-2 pb-24">
          <div className="flex-1 bg-white dark:bg-navy-700 rounded-2xl p-4 flex flex-row items-center text-center">
            <div>
              <span className="inline-block p-1 rounded-full bg-brand-500 text-white mb-8">
                <CheckIcon />
              </span>
              <p className="font-bold text-lg mb-2">Registration complete</p>
              <p className="text-sm">
                We have sent a check-in QR-code via SMS to all parents and
                guardians registered. Please show it to church staff during
                children check-in.
              </p>
            </div>
          </div>
          <DarkButton>View check-in QR code</DarkButton>
        </div>
      )}
    </>
  );
};

export default Registration;
